import { useEffect } from "react"

import { zodResolver } from "@hookform/resolvers/zod"
import { useForm, useWatch } from "react-hook-form"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"

import Icon from "@/components/Icon"
import IncentiveForm from "@/components/incentives/IncentiveForm"
import { STATUS_CLASSES, STATUS_LABELS } from "@/components/incentives/incentiveColumns"
import {
  type IncentiveFormValues,
  NEW_INCENTIVE,
  bodyFrom,
  fieldErrorsFrom,
  incentiveFormSchema,
  valuesFrom,
} from "@/components/incentives/incentiveFormSchema"
import { Alert, AlertDescription } from "@/components/ui/alert"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import {
  type Incentive,
  type IncentiveStatus,
  useChangeIncentiveStatus,
  useIncentive,
  useSaveIncentive,
} from "@/hooks/useIncentives"
import DashboardLayout from "@/layouts/DashboardLayout"

const STATUS_NOTES: Partial<Record<IncentiveStatus, string>> = {
  live: "This incentive is live. You can change its App Comms, set or extend its end date, end it or cancel it.",
  ended: "This incentive has ended, so it can't be changed.",
  cancelled: "This incentive was cancelled, so it can't be changed.",
}

// Copying opens the new incentive page with the source's values in the route state.
type CopyState = { copyOf?: IncentiveFormValues } | null

const sameIncentive = (a: IncentiveFormValues, b: IncentiveFormValues) =>
  JSON.stringify(bodyFrom(a)) === JSON.stringify(bodyFrom(b))

const LIVE_EDITABLE = ["end_date", "app_comms"] as const

// The API refuses a live incentive any other field, or an end date it already has, so only what
// changed goes.
const liveEditFrom = (incentive: Incentive, values: IncentiveFormValues) => {
  const saved = bodyFrom(valuesFrom(incentive))
  const edited = bodyFrom(values)
  return Object.fromEntries(
    LIVE_EDITABLE.filter(
      (field) => JSON.stringify(edited[field]) !== JSON.stringify(saved[field]),
    ).map((field) => [field, edited[field]]),
  )
}

const errorMessage = (error: unknown) => (error instanceof Error ? error.message : "Unknown error")

type ConfirmProps = {
  label: string
  title: string
  description: string
  disabled: boolean
  onConfirm: () => void
}

const ConfirmButton = ({ label, title, description, disabled, onConfirm }: ConfirmProps) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <Button type="button" variant="outline" disabled={disabled}>
        {label}
      </Button>
    </AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription>{description}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Go back</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm}>{label}</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
)

export default function IncentiveDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const copyOf = id ? undefined : (location.state as CopyState)?.copyOf
  const { data: incentive, isLoading, error } = useIncentive(id)
  const save = useSaveIncentive()
  const changeStatus = useChangeIncentiveStatus()

  const form = useForm<IncentiveFormValues>({
    resolver: zodResolver(incentiveFormSchema),
    defaultValues: copyOf ?? NEW_INCENTIVE,
  })
  const values = useWatch({ control: form.control })

  useEffect(() => {
    if (incentive) form.reset(valuesFrom(incentive))
  }, [incentive, form])

  // Copying from the new page stays on /incentives/new, so the form has to be reset by hand.
  useEffect(() => {
    if (!id) form.reset(copyOf ?? NEW_INCENTIVE)
  }, [id, copyOf, location.key, form])

  const unchanged = copyOf && sameIncentive(values as IncentiveFormValues, copyOf)

  const status = incentive?.status
  const editable = !status || status === "draft" ? "all" : status === "live" ? "live" : "none"
  const busy = save.isPending || changeStatus.isPending

  const showError = (error: unknown) => {
    const { fieldErrors, unmatched } = fieldErrorsFrom(errorMessage(error))
    fieldErrors.forEach(({ name, message }) => form.setError(name, { type: "server", message }))
    if (unmatched.length > 0) {
      toast.error("The incentive wasn't saved", { description: unmatched.join("; ") })
    }
  }

  const saveValues = (values: IncentiveFormValues) =>
    save.mutateAsync({
      id,
      body: editable === "live" ? liveEditFrom(incentive, values) : bodyFrom(values),
    })

  const onSubmit = async (values: IncentiveFormValues) => {
    try {
      const saved = await saveValues(values)
      toast.success(status === "live" ? "Changes saved" : "Saved as draft")
      if (!id) navigate(`/incentives/${saved.id}`, { replace: true })
    } catch (error) {
      showError(error)
    }
  }

  const setLive = form.handleSubmit(async (values) => {
    try {
      await saveValues(values)
      await changeStatus.mutateAsync({ id, status: "live" })
      toast.success("The incentive is live")
    } catch (error) {
      showError(error)
    }
  })

  const copy = (values: IncentiveFormValues) =>
    navigate("/incentives/new", { state: { copyOf: values } })

  const createAndCopy = form.handleSubmit(async (values) => {
    try {
      await saveValues(values)
      toast.success("Saved as draft. This is a copy of it.")
      copy(values)
    } catch (error) {
      showError(error)
    }
  })

  const moveTo = async (next: "ended" | "cancelled") => {
    try {
      await changeStatus.mutateAsync({ id, status: next })
      toast.success(next === "ended" ? "The incentive has ended" : "The incentive was cancelled")
    } catch (error) {
      toast.error("The incentive's status wasn't changed", { description: errorMessage(error) })
    }
  }

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-4xl">
        <Button
          variant="ghost"
          className="mb-4 flex items-center gap-2"
          onClick={() => navigate("/sheriff?tab=incentives")}
        >
          <Icon name="arrowLeft" className="h-4 w-4 text-current" />
          Back to incentives
        </Button>

        <div className="mb-4 flex items-center gap-3">
          <h1 className="text-2xl font-semibold text-palette-jungleGreen900">
            {incentive?.name ?? (id ? "Incentive" : "New incentive")}
          </h1>
          {status && (
            <Badge className={`pointer-events-none border-transparent ${STATUS_CLASSES[status]}`}>
              {STATUS_LABELS[status]}
            </Badge>
          )}
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <Icon name="warningCircleFilled" className="h-4 w-4" />
            <AlertDescription>Error loading the incentive: {errorMessage(error)}</AlertDescription>
          </Alert>
        )}

        {status && STATUS_NOTES[status] && (
          <Alert className="mb-4">
            <Icon name="infoCircle" className="h-4 w-4" />
            <AlertDescription>{STATUS_NOTES[status]}</AlertDescription>
          </Alert>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : (
          (!id || incentive) && (
            <IncentiveForm form={form} editable={editable} onSubmit={onSubmit} copyOf={copyOf}>
              {unchanged && (
                <Alert>
                  <Icon name="infoCircle" className="h-4 w-4" />
                  <AlertDescription>
                    This is a copy and nothing has changed yet. Saving it makes a second identical
                    incentive.
                  </AlertDescription>
                </Alert>
              )}
              <div className="flex flex-wrap justify-end gap-2">
                {id ? (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={busy}
                    onClick={() => copy(form.getValues())}
                  >
                    Copy
                  </Button>
                ) : (
                  <Button type="button" variant="outline" disabled={busy} onClick={createAndCopy}>
                    Create and copy
                  </Button>
                )}
                {editable !== "none" && (
                  <>
                    {status && (
                      <ConfirmButton
                        label="Cancel incentive"
                        title="Cancel this incentive?"
                        description="A cancelled incentive can't be set live again."
                        disabled={busy}
                        onConfirm={() => moveTo("cancelled")}
                      />
                    )}
                    {status === "live" && (
                      <ConfirmButton
                        label="End incentive"
                        title="End this incentive?"
                        description="It ends today and can't be set live again."
                        disabled={busy}
                        onConfirm={() => moveTo("ended")}
                      />
                    )}
                    {status === "draft" && (
                      <Button type="button" variant="outline" disabled={busy} onClick={setLive}>
                        Save and set live
                      </Button>
                    )}
                    <Button
                      type="submit"
                      disabled={busy || (status === "live" && !form.formState.isDirty)}
                    >
                      {status === "live" ? "Save changes" : "Save draft"}
                    </Button>
                  </>
                )}
              </div>
            </IncentiveForm>
          )
        )}
      </div>
    </DashboardLayout>
  )
}
